import { useEffect, useRef, useCallback, useState } from "react";
import {
  HubConnection,
  HubConnectionBuilder,
  LogLevel,
} from "@microsoft/signalr";
import { useAppSelector } from "src/app/hooks";
import { selectAuth } from "src/features/authSlice";

interface UseSignalROptions {
  hubUrl: string;
  groupName?: string;
  onMessage?: (methodName: string, ...args: any[]) => void;
}

interface UseSignalRReturn {
  connection: HubConnection | null;
  isConnected: boolean;
  joinGroup: (groupName: string) => Promise<void>;
  leaveGroup: (groupName: string) => Promise<void>;
}

export const useSignalR = ({
  hubUrl,
  groupName,
  onMessage,
}: UseSignalROptions): UseSignalRReturn => {
  const connectionRef = useRef<HubConnection | null>(null);
  const { isAuthenticated, acctId, loginId } = useAppSelector(selectAuth);
  const isConnectedRef = useRef(false);
  const currentGroupRef = useRef<string | null>(null);

  // Connection status is tracked in BOTH a ref and state. The ref is read
  // synchronously inside callbacks (joinGroup/leaveGroup) where a stale closure
  // would be wrong; the state drives re-renders so the UI updates the moment the
  // connection opens/closes. Returning only the ref (the previous behavior) meant
  // the "Live updates interrupted" banner lingered until an unrelated re-render,
  // because changing a ref never re-renders.
  const [isConnected, setIsConnected] = useState(false);
  const setConnected = useCallback((connected: boolean) => {
    isConnectedRef.current = connected;
    setIsConnected(connected);
  }, []);

  const joinGroup = useCallback(async (group: string) => {
    if (connectionRef.current && isConnectedRef.current) {
      try {
        await connectionRef.current.invoke("JoinGroup", group);
        currentGroupRef.current = group;
        console.log(`Joined SignalR group: ${group}`);
      } catch (error) {
        console.error(`Failed to join SignalR group ${group}:`, error);
      }
    }
  }, []);

  const leaveGroup = useCallback(async (group: string) => {
    if (connectionRef.current && isConnectedRef.current) {
      try {
        await connectionRef.current.invoke("LeaveGroup", group);
        if (currentGroupRef.current === group) {
          currentGroupRef.current = null;
        }
        console.log(`Left SignalR group: ${group}`);
      } catch (error) {
        console.error(`Failed to leave SignalR group ${group}:`, error);
      }
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !acctId || !loginId) {
      return;
    }

    // Build the connection
    const connection = new HubConnectionBuilder()
      .withUrl(hubUrl, {
        headers: {
          acctId: acctId,
          loginId: loginId,
        },
      })
      .withAutomaticReconnect([0, 2000, 10000, 30000])
      .configureLogging(LogLevel.Information)
      .build();

    connectionRef.current = connection;

    // Set up connection event handlers
    connection.onreconnecting(() => {
      console.log("SignalR connection lost. Attempting to reconnect...");
      setConnected(false);
    });

    connection.onreconnected(() => {
      console.log("SignalR connection restored.");
      setConnected(true);
      // Rejoin group if we were in one
      if (currentGroupRef.current) {
        joinGroup(currentGroupRef.current);
      }
    });

    connection.onclose(() => {
      console.log("SignalR connection closed.");
      setConnected(false);
      currentGroupRef.current = null;
    });

    // Set up message handler if provided
    if (onMessage) {
      // Listen to all methods and forward to the callback
      connection.on("NewCdrAnalysis", (...args) => {
        onMessage("NewCdrAnalysis", ...args);
      });

      // You can add more specific message handlers here as needed
      // connection.on("OtherMessage", (...args) => {
      //   onMessage("OtherMessage", ...args);
      // });
    }

    // Start the connection
    const startConnection = async () => {
      try {
        console.log(`Attempting to connect to SignalR hub: ${hubUrl}`);
        await connection.start();
        console.log("SignalR connection established successfully");
        setConnected(true);

        // Join initial group if provided
        if (groupName) {
          await joinGroup(groupName);
        }
      } catch (error: any) {
        console.error("Failed to establish SignalR connection:", error);
        console.error("Hub URL:", hubUrl);
        console.error("Error details:", {
          message: error.message,
          name: error.name,
          stack: error.stack,
        });

        // More specific error handling
        if (error.message?.includes("404")) {
          console.error("❌ SignalR Hub not found (404). Check:");
          console.error("1. Hub URL:", hubUrl);
          console.error("2. Hub name: 'cdrAnalysisHub'");
          console.error("3. Server is running and hub is registered");
        }

        setConnected(false);
      }
    };

    startConnection();

    // Cleanup function
    return () => {
      connection?.stop().catch(console.error);
      isConnectedRef.current = false;
      currentGroupRef.current = null;
    };
  }, [
    isAuthenticated,
    acctId,
    loginId,
    hubUrl,
    onMessage,
    groupName,
    joinGroup,
    setConnected,
  ]);

  return {
    connection: connectionRef.current,
    isConnected,
    joinGroup,
    leaveGroup,
  };
};
